import { useCallback, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Link, Upload } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const RichTextEditor = ({ value, onChange, placeholder = "Tulis konten artikel..." }: RichTextEditorProps) => {
  const quillRef = useRef<ReactQuill>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const { toast } = useToast();

  // Memoize modules to prevent unnecessary re-renders
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        ['link', 'image'],
        [{ 'align': [] }],
        [{ 'color': [] }, { 'background': [] }],
        ['blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        image: () => setImageDialogOpen(true)
      }
    },
    clipboard: {
      matchVisual: false,
    }
  }), []);

  // Memoize formats to prevent unnecessary re-renders
  const formats = useMemo(() => [
    'header', 'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'link', 'image', 'align',
    'color', 'background',
    'blockquote', 'code-block'
  ], []);

  // Memoize the insertImageFromUrl function
  const insertImageFromUrl = useCallback(() => {
    if (!imageUrl.trim()) {
      toast({
        title: "Error",
        description: "Masukkan URL gambar yang valid",
        variant: "destructive",
      });
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (quill) {
      const range = quill.getSelection();
      quill.insertEmbed(range?.index || 0, 'image', imageUrl);
      quill.setSelection((range?.index || 0) + 1, 0); // Move cursor after the image
    }
    
    setImageUrl('');
    setImageDialogOpen(false);
  }, [imageUrl, toast]);

  // Memoize the uploadImage function
  const uploadImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Error",
        description: "File harus berupa gambar",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast({
        title: "Error",
        description: "Ukuran file maksimal 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingImage(true);
    
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection();
        quill.insertEmbed(range?.index || 0, 'image', publicUrl);
        quill.setSelection((range?.index || 0) + 1, 0); // Move cursor after the image
      }

      setImageDialogOpen(false);
      toast({
        title: "Berhasil",
        description: "Gambar berhasil diunggah",
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Gagal mengunggah gambar: " + (error instanceof Error ? error.message : 'Unknown error'),
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  }, [toast]);

  // Memoize the file change handler
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadImage(file);
    }
    // Reset the input to allow selecting the same file again
    event.target.value = '';
  }, [uploadImage]);

  // Memoize the onChange handler
  const handleEditorChange = useCallback((content: string) => {
    // Only call onChange if content actually changed
    if (content !== value) {
      onChange(content);
    }
  }, [value, onChange]);

  return (
    <div className="space-y-4">
      <div className="min-h-[300px] bg-white rounded-lg overflow-hidden">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={handleEditorChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          className="h-full"
          style={{ height: '100%' }}
          preserveWhitespace
        />
      </div>

      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Tambah Gambar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* URL Input */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    <Label htmlFor="image-url">Dari URL</Label>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      id="image-url"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && insertImageFromUrl()}
                      className="flex-1"
                    />
                    <Button 
                      onClick={insertImageFromUrl}
                      disabled={!imageUrl.trim()}
                    >
                      Sisipkan
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* File Upload */}
            <Card>
              <CardContent className="p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Upload className="w-4 h-4" />
                    <Label htmlFor="file-upload">Upload File</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      disabled={uploadingImage}
                      className="cursor-pointer"
                    />
                  </div>
                  {uploadingImage && (
                    <div className="text-sm text-muted-foreground animate-pulse">
                      Mengunggah gambar...
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichTextEditor;